import { useMemo } from 'react';
import { useAuthContext } from './useAuthContext';

// Required fields configuration for profile completion
const REQUIRED_FIELDS = [
  { 
    id: 'firstName', 
    path: 'basicInfo.firstName', 
    label: 'First Name',
    description: 'Required for professional networking',
    scrollTarget: 'general-info-section'
  },
  { 
    id: 'lastName', 
    path: 'basicInfo.lastName', 
    label: 'Last Name',
    description: 'Complete your professional identity',
    scrollTarget: 'general-info-section'
  },
  { 
    id: 'profilePicture', 
    paths: ['profilePicture', 'basicInfo.photoURL', 'photoURL'], 
    label: 'Profile Picture',
    description: 'Add a professional photo',
    scrollTarget: 'profile-picture-section'
  },
  { 
    id: 'currentRole', 
    path: 'basicInfo.currentRole', 
    label: 'Current Role',
    description: 'Helps others understand your expertise',
    scrollTarget: 'general-info-section'
  },
  { 
    id: 'companyName', 
    path: 'basicInfo.companyName', 
    label: 'Company',
    description: 'Provides professional context',
    scrollTarget: 'general-info-section'
  },
  { 
    id: 'location', 
    path: 'basicInfo.location', 
    label: 'Location',
    description: 'Enables local networking opportunities',
    scrollTarget: 'general-info-section'
  },
  { 
    id: 'linkedin', 
    path: 'basicInfo.linkedinUrl', 
    label: 'LinkedIn Profile',
    description: 'Essential for professional credibility',
    scrollTarget: 'general-info-section'
  }
];

/**
 * Helper function to get nested object value by path
 * @param {Object} obj - The object to search
 * @param {string} path - Dot notation path (e.g., 'basicInfo.firstName')
 * @returns {*} The value at the path, or undefined
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

/**
 * Check if a field value is considered "complete"
 * @param {*} value - The field value to check
 * @returns {boolean} True if the field has a meaningful value
 */
const isFieldComplete = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Custom hook to calculate and manage profile completion state
 * @returns {Object} Profile completion data
 */
export const useProfileCompletion = () => {
  const { user, userProfile } = useAuthContext();

  const completionData = useMemo(() => {
    // If no user profile, return default state
    if (!userProfile) {
      return {
        completionPercentage: 0,
        missingFields: REQUIRED_FIELDS.map(f => f.id),
        isComplete: false,
        requiredFields: REQUIRED_FIELDS,
        completedCount: 0,
        totalCount: REQUIRED_FIELDS.length,
        refreshCompletion: () => {}
      };
    }

    // Check each required field
    const fieldStatuses = REQUIRED_FIELDS.map(field => {
      let isComplete = false;

      // Handle fields with multiple possible paths (like profile picture)
      if (field.paths) {
        isComplete = field.paths.some(path => {
          const value = getNestedValue(userProfile, path);
          return isFieldComplete(value);
        });
        
        // Special handling for profile picture: also check Firebase Auth user.photoURL
        if (field.id === 'profilePicture' && !isComplete && user?.photoURL) {
          isComplete = isFieldComplete(user.photoURL);
        }
      } else {
        // Single path field
        const value = getNestedValue(userProfile, field.path);
        isComplete = isFieldComplete(value);
      }

      return {
        ...field,
        isComplete
      };
    });

    // Calculate completion metrics
    const completedCount = fieldStatuses.filter(f => f.isComplete).length;
    const totalCount = REQUIRED_FIELDS.length;
    const completionPercentage = Math.round((completedCount / totalCount) * 100);
    const missingFields = fieldStatuses
      .filter(f => !f.isComplete)
      .map(f => f.id);
    const isComplete = completionPercentage === 100;

    return {
      completionPercentage,
      missingFields,
      isComplete,
      requiredFields: REQUIRED_FIELDS,
      completedCount,
      totalCount,
      fieldStatuses
    };
  }, [user, userProfile]);

  return completionData;
};

export default useProfileCompletion;
